import React, { useEffect, useMemo, useState } from 'react';
import { listArticles, KbArticle } from '../../../../services/knowledgeBaseApi';
import styles from './TicketKnowledgeBasePanel.module.css';

interface TicketKnowledgeBasePanelProps {
  /** Ticket module label (e.g. "Payment"), used to auto-suggest articles by category. */
  category: string;
  /** Ticket subject, used only to pre-seed the manual search box. */
  subject: string;
}

/**
 * Contextual Knowledge Base panel (VP-CS-004): lets a Call Center agent
 * search KB articles, or see ones auto-suggested by the ticket's module,
 * without leaving the ticket detail view. Reuses the same
 * `knowledgeBaseApi.listArticles` call the standalone KB page
 * (CallCenterKnowledgeBase.tsx) uses.
 */
export const TicketKnowledgeBasePanel: React.FC<TicketKnowledgeBasePanelProps> = ({ category, subject }) => {
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState('');
  const [suggested, setSuggested] = useState<KbArticle[]>([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [results, setResults] = useState<KbArticle[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Auto-suggest: articles filed under this ticket's module/category.
  useEffect(() => {
    if (!open || !category) return;
    let cancelled = false;
    setSuggestedLoading(true);
    listArticles({ category })
      .then((articles) => { if (!cancelled) setSuggested(articles); })
      .catch(() => { if (!cancelled) setSuggested([]); })
      .finally(() => { if (!cancelled) setSuggestedLoading(false); });
    return () => { cancelled = true; };
  }, [open, category]);

  // Manual search, debounced.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); setError(null); return; }
    setSearching(true);
    const handle = setTimeout(() => {
      listArticles({ search: q })
        .then((articles) => { setResults(articles); setError(null); })
        .catch((err) => { setResults([]); setError(err instanceof Error ? err.message : 'Search failed'); })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const listToShow = results !== null ? results : suggested;
  const isSearchMode = results !== null;
  const loading = isSearchMode ? searching : suggestedLoading;

  const heading = useMemo(() => {
    if (isSearchMode) return `Results for "${query.trim()}"`;
    return category ? `Suggested for ${category} tickets` : 'Suggested articles';
  }, [isSearchMode, query, category]);

  return (
    <div className={styles.panel}>
      <button type="button" className={styles.header} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={styles.title}>Knowledge Base</span>
        <span className={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className={styles.body}>
          <div className={styles.searchWrap}>
            <input
              className={styles.input}
              placeholder={`Search knowledge base... (e.g. "${subject.slice(0, 40)}")`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <p className={styles.sectionLabel}>{heading}</p>

          {loading && <p className={styles.empty}>Loading…</p>}
          {error && <p className={styles.error}>{error}</p>}
          {!loading && !error && listToShow.length === 0 && (
            <p className={styles.empty}>
              {isSearchMode ? 'No articles match that search.' : 'No suggested articles for this module yet — try searching above.'}
            </p>
          )}

          <div className={styles.list}>
            {!loading && listToShow.map((article) => {
              const expanded = expandedId === article.id;
              return (
                <div key={article.id} className={styles.card}>
                  <button
                    type="button"
                    className={styles.cardHeader}
                    onClick={() => setExpandedId(expanded ? null : article.id)}
                  >
                    <span className={styles.cardTitle}>{article.title}</span>
                    <span className={styles.cardMeta}>
                      {article.category} · {article.read_time_minutes} min read
                    </span>
                  </button>
                  {expanded && <p className={styles.cardBody}>{article.body}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketKnowledgeBasePanel;
